/**
 * Context Types
 *
 * Defines the multi-context model where a user can simultaneously hold
 * roles in different contexts: personal, school, business, and platform.
 *
 * Key principles:
 * - Everyone is a "user" first (personal context always exists)
 * - Roles are additional capabilities layered on top via contexts
 * - School admins are completely separate from platform admins
 * - "Parent" is a relationship (linkedChildIds), not a context
 */

import type { UUID } from './common.types'
import type { AdminRole } from './admin.types'

/**
 * The four context types a user can hold
 */
export type ContextType = 'personal' | 'school' | 'business' | 'platform'

/**
 * Roles within a school context
 */
export type SchoolRole = 'student' | 'teacher' | 'school_admin' | 'head_school_admin'

/**
 * Roles within a business context (for vendors / challenge creators)
 */
export type BusinessRole = 'owner' | 'manager' | 'contributor'

/**
 * Roles within the platform context (DoCurious internal)
 */
export type PlatformRole = 'staff' | 'admin'

/**
 * Personal context — always exists for every user.
 * Challenges, track records, XP, badges, communities all live here.
 */
export interface PersonalContext {
  type: 'personal'
}

/**
 * School context — optional, managed by a school.
 * Goes away if user leaves the school, but personal stuff stays.
 */
export interface SchoolContext {
  type: 'school'
  role: SchoolRole
  schoolId: UUID
  schoolName?: string
  /** Account tier — only relevant for students (COPPA compliance) */
  tier?: 'tier_1_school_only' | 'tier_2_full'
}

/**
 * Business context — optional, for vendors and challenge business managers.
 * Multiple people can share the same businessId with different roles.
 */
export interface BusinessContext {
  type: 'business'
  role: BusinessRole
  businessId: UUID
  businessName?: string
}

/**
 * Platform context — optional, DoCurious internal staff/admins.
 * Completely separate from school administration.
 */
export interface PlatformContext {
  type: 'platform'
  role: PlatformRole
  /** Granular admin sub-role — only when role === 'admin' */
  adminSubRole?: AdminRole
}

/**
 * All contexts a user can hold simultaneously.
 * Personal is always present; others are optional.
 */
export interface UserContexts {
  personal: PersonalContext
  school?: SchoolContext
  business?: BusinessContext
  platform?: PlatformContext
}

/**
 * Display names for context types
 */
export const ContextDisplayNames: Record<ContextType, string> = {
  personal: 'Personal',
  school: 'School',
  business: 'Business',
  platform: 'Platform',
}

/**
 * Display names for school roles
 */
export const SchoolRoleDisplayNames: Record<SchoolRole, string> = {
  student: 'Student',
  teacher: 'Teacher',
  school_admin: 'School Admin',
  head_school_admin: 'Head School Admin',
}

/**
 * Display names for business roles
 */
export const BusinessRoleDisplayNames: Record<BusinessRole, string> = {
  owner: 'Owner',
  manager: 'Manager',
  contributor: 'Contributor',
}

/**
 * Display names for platform roles
 */
export const PlatformRoleDisplayNames: Record<PlatformRole, string> = {
  staff: 'Staff',
  admin: 'Admin',
}
